import { IVideo } from "@faire/web-api/indigofair/data/IVideo";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import isChromatic from "chromatic";
import React, { useState } from "react";
import styled, { css } from "styled-components";

import {
  determineVideoGridPositioning,
  getVideoAspectRatio,
  ICollectionTileContainerProps,
  ICollectionVideoDimensions,
  ICollectionVideoTileContainerStyledPropTypes,
} from "@faire/retailer/views/product-based-collection/__internal__/__internal__/utils";
import { LazyVideoPlayer } from "@faire/retailer/views/video/components/VideoPlayer";

interface ICollectionVideoTilePropTypes extends ICollectionTileContainerProps {
  video: IVideo;
  onFailedToPlayVideo?: () => void;
}

export const CollectionVideoTile: React.FC<ICollectionVideoTilePropTypes> = ({
  columnCount,
  tileCount,
  video,
  onFailedToPlayVideo,
}) => {
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState<
    ICollectionVideoDimensions | undefined
  >(undefined);
  const ref = useDoActionWhenVisible(() => setIsVideoVisible(true));

  return (
    <CollectionVideoTileContainer
      $columnCount={columnCount}
      $tileCount={tileCount}
      $videoDimensions={videoDimensions}
      data-test-id="collection-video-tile"
      ref={ref}
    >
      {/* Video player triggers chromatic changes */}
      {!isChromatic() ? (
        <LazyVideoPlayer
          video={video}
          videoOptions={{
            autoplay: false,
            loop: false,
            muted: true,
            bigPlayButton: true,
          }}
          startPlayVideo={isVideoVisible}
          onFailedToPlayVideo={onFailedToPlayVideo}
          onLoadedMetaData={(player) => {
            setVideoDimensions({
              width: player.videoWidth(),
              height: player.videoHeight(),
            });
          }}
        />
      ) : null}
    </CollectionVideoTileContainer>
  );
};

const CollectionVideoTileContainer = styled.div<ICollectionVideoTileContainerStyledPropTypes>`
  ${(props) => {
    const positioning = determineVideoGridPositioning(props);

    return css`
      grid-column: ${positioning.gridColumn};
      grid-row: ${positioning.gridRow};

      ${responsiveMediaQueries.mobile} {
        aspect-ratio: ${getVideoAspectRatio(props.$videoDimensions)};
      }

      .vjs-tech {
        object-fit: cover;
      }
    `;
  }}
`;
